import React, { useState, useEffect, useRef } from "react";
import { Box, Grid, Paper, Typography, Card, CardMedia, IconButton } from "@mui/material";
import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";
import ZoomIn from "@mui/icons-material/ZoomIn";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { investorApi, vendorApi } from "../../../api/api";
import DeleteIcon from "@mui/icons-material/Delete";
import { Snackbar, Alert } from "@mui/material";
import Close from "@mui/icons-material/Close";
import {
    Dialog,
    DialogContent,
} from "@mui/material";
/* ---------- Helper: Parse Amenities ---------- */
const parseAmenities = (amenities) => {
    try {
        return amenities ? JSON.parse(amenities) : [];
    } catch (err) {
        return [];
    }
};

const YourApartmentOverviewDetails = ({ data }) => {
    const amenitiesList = parseAmenities(data?.Amenities);

    const [images, setImages] = useState([]);
    const [openImage, setOpenImage] = useState(null);

    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const scrollRef = useRef(null);
    const showNavigation = images.length > 1;
    const scroll = (direction) => {
        if (!scrollRef.current) return;

        const container = scrollRef.current;
        const scrollAmount = container.offsetWidth * 0.4;

        container.scrollBy({
            left: direction === "left" ? -scrollAmount : scrollAmount,
            behavior: "smooth",
        });
    };

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });
    useEffect(() => {
        if (data?.id) {
            getImages();
        }
    }, [data?.id]);

    const getImages = async () => {
        try {
            const response = await investorApi.getApartmentImagesById(data.id);
            setImages(response?.data || []);
        } catch (error) {
            console.error("Failed to fetch images", error);
            setImages([]);
        }
    };

    const handleOpenImage = (img, index) => {
        setOpenImage(img.ImageURL);
        setCurrentImageIndex(index);
    };

    const handleCloseImage = () => {
        setOpenImage(null);
    };

    const handlePrevImage = () => {
        const prevIndex =
            (currentImageIndex - 1 + images.length) % images.length;

        setCurrentImageIndex(prevIndex);
        setOpenImage(images[prevIndex].ImageURL);
    };

    const handleNextImage = () => {
        const nextIndex = (currentImageIndex + 1) % images.length;

        setCurrentImageIndex(nextIndex);
        setOpenImage(images[nextIndex].ImageURL);
    };
    const handleDeleteImage = async (id) => {
        try {
            await vendorApi.deleteImageById({
                id: id,
                type: 3
            });

            setImages((prev) => prev.filter((img) => img.id !== id));

            setSnackbar({
                open: true,
                message: "Image deleted successfully",
                severity: "success",
            });

            setTimeout(() => {
                getImages();
            }, 1000);

        } catch (error) {
            console.error("Failed to delete image", error);

            setSnackbar({
                open: true,
                message: "Failed to delete image",
                severity: "error",
            });
        }
    };

    return (
        <>
            <Box
                sx={{
                    marginTop: 3,
                    backgroundColor: "#ffffff", // ✅ screen white
                    p: { xs: 2, md: 3 },
                    borderRadius: 1,
                }}
            >
                <Typography variant="h5" fontWeight={700} mb={2}>
                    Amenities
                </Typography>

                {amenitiesList.length === 0 ? (
                    <Typography color="text.secondary">
                        No amenities available
                    </Typography>
                ) : (
                    <Grid container spacing={2}>
                        {amenitiesList.map((item, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                                <Paper
                                    elevation={1}
                                    sx={{
                                        maxWidth: {
                                            xs: "9rem",
                                            sm: "100%",
                                            lg: "100%"
                                        },
                                        p: { xs: 1, sm: 2 },
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        borderRadius: 2,
                                        backgroundColor: "#fff",
                                    }}
                                >

                                    <CheckCircleIcon color="success" sx={{
                                        fontSize: {
                                            xs: "12px",
                                            sm: "18px",
                                            lg: "18px"
                                        }
                                    }} />
                                    <Typography fontWeight={500} sx={{
                                        fontSize: {
                                            xs: "12px",
                                            sm: "16px",
                                            lg: "16px"
                                        }
                                    }}>
                                        {item.name}
                                    </Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Box>
            {data?.Description && (
                <Box mb={3} sx={{
                    borderRadius: 1, marginTop: 3,
                    backgroundColor: "#ffffff", // ✅ screen white
                    p: { xs: 2, md: 3 },
                }} >
                    <Typography variant="h5" fontWeight={700} mb={1}>
                        Description
                    </Typography>

                    <Typography
                        color="text.secondary"
                        sx={{ lineHeight: 1.7 }}
                    >
                        {data.Description}
                    </Typography>
                </Box>
            )}
            <Box
                sx={{
                    position: "relative",
                    mt: 4,
                    backgroundColor: "#fff",
                    p: 3,
                    borderRadius: 2,
                }}
            >
                <Typography variant="h5" fontWeight={700} mb={2}>
                    Media Gallery
                </Typography>

                {/* Left Arrow */}
                <IconButton
                    onClick={() => scroll("left")}
                    sx={{
                        position: "absolute",
                        left: -15,
                        top: "50%",
                        transform: "translateY(-50%)",
                        backgroundColor: "white",
                        boxShadow: 2,
                        zIndex: 10,
                    }}
                >
                    <ArrowBackIos fontSize="small" />
                </IconButton>

                {/* Right Arrow */}
                <IconButton
                    onClick={() => scroll("right")}
                    sx={{
                        position: "absolute",
                        right: -15,
                        top: "50%",
                        transform: "translateY(-50%)",
                        backgroundColor: "white",
                        boxShadow: 2,
                        zIndex: 10,
                    }}
                >
                    <ArrowForwardIos fontSize="small" />
                </IconButton>

                {/* Images */}
                <Box
                    ref={scrollRef}
                    sx={{
                        display: "flex",
                        gap: 2,
                        overflowX: "auto",
                        scrollBehavior: "smooth",
                        "&::-webkit-scrollbar": { display: "none" },
                    }}
                >
                    {images.map((img, index) => (
                        <Card
                            key={index}
                            onClick={() => handleOpenImage(img, index)}
                            sx={{
                                flexShrink: 0,
                                borderRadius: 2,
                                width: { xs: "85%", sm: "45%", md: "28%" },
                                height: 220,
                                cursor: "pointer",
                                position: "relative"
                            }}
                        >
                            {/* Delete Icon */}
                            <IconButton
                                onClick={(e) => {
                                    e.stopPropagation(); // prevent modal opening
                                    handleDeleteImage(img.Id);
                                }}
                                sx={{
                                    position: "absolute",
                                    top: 8,
                                    right: 8,
                                    backgroundColor: "rgba(0,0,0,0.6)",
                                    color: "white",
                                    zIndex: 5,
                                    "&:hover": {
                                        backgroundColor: "rgba(255,0,0,0.8)"
                                    }
                                }}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>

                            <CardMedia
                                component="img"
                                image={img.ImageURL}
                                alt={`Apartment ${index}`}
                                sx={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                }}
                            />
                        </Card>
                    ))}
                </Box>
            </Box>


            <Dialog
                open={!!openImage}
                onClose={handleCloseImage}
                maxWidth={false}
                fullScreen
            >
                <DialogContent
                    sx={{
                        p: 0,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                        backgroundColor: "rgba(0,0,0,0.9)",
                        position: "relative"
                    }}
                >
                    {/* Close */}
                    <IconButton
                        onClick={handleCloseImage}
                        sx={{
                            position: "absolute",
                            top: 20,
                            right: 20,
                            color: "white"
                        }}
                    >
                        <Close />
                    </IconButton>

                    {/* Prev */}
                    {showNavigation && (
                        <IconButton
                            onClick={handlePrevImage}
                            sx={{
                                position: "absolute",
                                left: 20,
                                top: "50%",
                                transform: "translateY(-50%)",
                                color: "white"
                            }}
                        >
                            <ArrowBackIos />
                        </IconButton>
                    )}

                    {showNavigation && (
                        <IconButton
                            onClick={handleNextImage}
                            sx={{
                                position: "absolute",
                                right: 20,
                                top: "50%",
                                transform: "translateY(-50%)",
                                color: "white"
                            }}
                        >
                            <ArrowForwardIos />
                        </IconButton>
                    )}


                    {/* Image */}
                    {openImage && (
                        <Box
                            component="img"
                            src={openImage}
                            sx={{
                                maxWidth: "95%",
                                maxHeight: "95%",
                                objectFit: "contain"
                            }}
                        />
                    )}
                </DialogContent>
            </Dialog>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default YourApartmentOverviewDetails;
